// Ctrl+K / ⌘K archive search. ARIA 1.2 combobox + listbox inside a modal
// <dialog>: type to filter, ↑/↓ to move, Enter to open, Esc to close.
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { CONTENT_TYPE_LABELS } from "../../shared/model.ts";
import { isNew } from "../../shared/flags.ts";
import { PRODUCT_BY_ID } from "../../shared/products.ts";
import { normalizeText, search, type SearchEntry } from "../../shared/searchIndex.ts";
import { navigate } from "../hooks/useRouter.ts";
import { formatDate } from "../lib/briefing.ts";
import { useSearchIndex } from "../lib/searchStore.ts";
import { useApp } from "../state/AppState.tsx";
import { FlagChip } from "./FlagChip.tsx";
import { btn } from "./ui.tsx";

export const isMac = () => typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
/** Dispatch on window to open the site search from anywhere (e.g. the Browse search button). */
export const OPEN_SEARCH_EVENT = "pulse:open-search";

export const shortcutLabel = () => (isMac() ? "⌘K" : "Ctrl K");

export function CommandPalette({ open, onClose }: { open: boolean; onClose(): void }) {
  const app = useApp();
  const ref = useRef<HTMLDialogElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const idx = useSearchIndex(app.repo, open);

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      d.showModal();
      setQuery("");
      setActive(0);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
    if (!open && d.open) d.close();
  }, [open]);

  const results: SearchEntry[] = useMemo(() => {
    if (!idx.index) return [];
    if (!query.trim()) return idx.index.doc.entries.slice(0, 8);
    return search(idx.index.prepared, query, 30).map((h) => h.entry);
  }, [idx.index, query]);

  useEffect(() => setActive(0), [query]);
  useEffect(() => {
    document.getElementById(`${listId}-${active}`)?.scrollIntoView({ block: "nearest" });
  }, [active, listId]);

  const openResult = (e: SearchEntry | undefined) => {
    if (!e) return;
    onClose();
    navigate(`/story/${e.id}`);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      openResult(results[active]);
    }
  };

  const total = idx.index?.doc.entries.length ?? 0;
  const terms = normalizeText(query).split(" ").filter(Boolean);

  return (
    <dialog
      ref={ref}
      aria-label="Search all updates"
      onClose={onClose}
      onClick={(e) => e.target === e.currentTarget && onClose()}
      className="sheet m-auto mt-[8vh] w-[min(42rem,94vw)] overflow-hidden rounded-xl border border-line bg-paper p-0 text-ink shadow-2xl"
    >
      <div className="flex items-center gap-3 border-b border-line px-4">
        <svg aria-hidden="true" viewBox="0 0 24 24" className="size-5 shrink-0 text-ink-soft" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <circle cx="11" cy="11" r="6.5" />
          <path d="m20 20-4-4" />
        </svg>
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-label="Search all updates"
          aria-expanded={results.length > 0}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={results.length ? `${listId}-${active}` : undefined}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Search updates"
          className="h-14 flex-1 bg-transparent text-base outline-none placeholder:text-ink-soft/80"
          autoComplete="off"
          spellCheck={false}
        />
        <kbd className="hidden rounded border border-line-strong px-1.5 py-0.5 text-2xs text-ink-soft sm:inline">Esc</kbd>
        <button type="button" onClick={onClose} className={`${btn.ghost} -mr-2 sm:hidden`} aria-label="Close search">
          Close
        </button>
      </div>

      <div className="max-h-[min(60dvh,30rem)] overflow-y-auto">
        {idx.status === "loading" || idx.status === "idle" ? (
          <p className="px-4 py-6 text-sm text-ink-soft" role="status">Loading the search index…</p>
        ) : idx.status === "error" ? (
          <div className="px-4 py-6 text-sm" role="alert">
            <p>{idx.error}</p>
            <button type="button" className={`${btn.secondary} mt-3`} onClick={idx.retry}>Try again</button>
          </div>
        ) : idx.status === "missing" ? (
          <p className="px-4 py-6 text-sm text-ink-soft">The search index hasn't been generated yet. It's built when stories are published.</p>
        ) : (
          <>
            {!query.trim() && <p className="px-4 pt-3 text-2xs font-bold uppercase tracking-wider text-ink-soft">Latest updates</p>}
            <ul id={listId} role="listbox" aria-label="Search results" className="p-2">
              {results.map((e, i) => (
                <li
                  key={e.id}
                  id={`${listId}-${i}`}
                  role="option"
                  aria-selected={i === active}
                  onMouseMove={() => setActive(i)}
                  onClick={() => openResult(e)}
                  className={`cursor-pointer rounded-xl px-3 py-2.5 ${i === active ? "bg-coral-wash" : ""}`}
                >
                  <p className="text-sm font-semibold leading-snug">
                    <Highlight text={e.t} terms={terms} />
                  </p>
                  <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-ink-soft">
                    <span>{formatDate(e.d)}</span>
                    <span aria-hidden="true">·</span>
                    <span>{CONTENT_TYPE_LABELS[e.ty]}</span>
                    {e.p.slice(0, 2).map((p) => (
                      <span key={p} className="rounded bg-sand px-1.5">{PRODUCT_BY_ID.get(p)?.name ?? p}</span>
                    ))}
                    {isNew(e.d) && <FlagChip status="new" />}
                    {e.f && <FlagChip status={e.f} />}
                    {e.x && <span className="font-semibold text-coral-deep">Fictional sample</span>}
                  </p>
                </li>
              ))}
            </ul>
            {query.trim() && results.length === 0 && (
              <p className="px-4 pb-6 text-sm text-ink-soft">No updates match “{query.trim()}”. Try fewer words or a product name.</p>
            )}
          </>
        )}
      </div>

      <p className="flex flex-wrap items-center justify-between gap-2 border-t border-line px-4 py-2 text-2xs text-ink-soft">
        <span className="hidden sm:inline">↑ ↓ to move · Enter to open · Esc to close</span>
        <span className="sm:hidden">Tap a result to open it</span>
        <span aria-live="polite">
          {idx.status === "ready" && (query.trim() ? `${results.length}${results.length === 30 ? "+" : ""} matches in ` : "Searching ")}
          {idx.status === "ready" && `all ${total} published updates`}
          {idx.index?.doc.truncated && " (oldest not indexed)"}
        </span>
      </p>
    </dialog>
  );
}

/** Wraps matching term prefixes in <mark>. */
export function Highlight({ text, terms }: { text: string; terms: string[] }) {
  if (!terms.length) return <>{text}</>;
  const re = new RegExp(`(${terms.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`, "gi");
  return (
    <>
      {text.split(re).map((part, i) =>
        i % 2 ? (
          <mark key={i} className="rounded bg-coral/20 px-0.5 text-ink">{part}</mark>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  );
}
